<script setup lang="ts">
import { buyCountBtn, commerceNameList } from './data'
import { useCommonStore } from '@/stores/modules/common'
import { getCommerceListApi } from '@/api/commerce'
import { computed, onMounted, ref, watch } from 'vue'
import type { CommerceShowItem } from '@/types/commerce'
import { useUserStore } from '@/stores/modules/user'
import { socketEmitAsync } from '@/utils/web-socket'
import { formatNumberWithCommas } from '@/utils'
import { QuestionFilled } from '@element-plus/icons-vue'
import { dayjs } from 'element-plus'
import { getCurrentInstance } from 'vue'
import Trend from './components/Trend.vue'

const envMode = import.meta.env.VITE_ENV_MODE

const commonStore = useCommonStore()
const userStore = useUserStore()

const maxBuyCount = envMode === 'test' ? 20000 : 10000

const tradeShow = ref(false)
const tradeType = ref<'buy' | 'sell'>()
const commerceList = ref<CommerceShowItem[]>()
async function getCommerceList() {
  const res = await getCommerceListApi()
  commerceList.value = res.map((item) => ({
    ...item,
    ...commerceNameList[item.commerceId],
  }))
  commerceList.value.sort((a, b) => (a.commerceId > b.commerceId ? 1 : -1))
}

const buyCount = ref(0)
const curCommerce = ref<CommerceShowItem>()
function handleShowTrade(type: 'buy' | 'sell', commerce: CommerceShowItem) {
  if (type === 'buy' && userTotalBuyCount.value >= maxBuyCount) return ElMessage.warning('购入达到限额')
  else if (type === 'sell' && getBuyCountById(commerce.commerceId) <= 0) return ElMessage.warning('您未持有该商会的商品')
  buyCount.value = 0
  curCommerce.value = commerce
  tradeType.value = type
  tradeShow.value = true
}
function handleBuy() {
  if (!curCommerce.value) return ElMessage.warning('未选择任何商会')
  if (buyCount.value <= 0) return ElMessage.warning('购入数量需大于0')
  const { commerceId, name, classes, price } = curCommerce.value
  const totalPrice = price * buyCount.value
  if (userStore.gold < totalPrice) return ElMessage.warning('您的金币不足')
  ElMessageBox.confirm(
    `<p class="affix">确定购买【<span class="${classes}">${name}</span>】的<span class="info">${
      buyCount.value
    }</span>份商品吗？（本次购买花费<span class="gold">${formatNumberWithCommas(totalPrice)}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const id = commerceId
      const count = buyCount.value
      await socketEmitAsync({ type: 'buyCommerceItem', data: { id, count } })
      const commerce = commerceList.value?.find((item) => item.commerceId === id)
      if (!commerce) return ElMessage.warning('未找到商会')
      const userCommerce = userStore.commerceList?.find((item) => item.id === id)
      if (!userCommerce) {
        userStore.commerceList?.push({ id, count, avgPrice: commerce.price })
      } else {
        userCommerce.avgPrice = Number(
          ((userCommerce.avgPrice * userCommerce.count + commerce.price * count) / (userCommerce.count + count)).toFixed(4),
        )
        userCommerce.count += count
      }
      userStore.gold -= totalPrice
      tradeShow.value = false
      getCurrentInstance()?.proxy?.$forceUpdate()
    })
    .catch(() => {})
}
function handleSell() {
  if (!curCommerce.value) return ElMessage.warning('未选择任何商会')
  if (buyCount.value <= 0) return ElMessage.warning('售出数量需大于0')
  const { commerceId, name, classes, price } = curCommerce.value
  const totalPrice = price * buyCount.value
  ElMessageBox.confirm(
    `<p class="affix">确定售出【<span class="${classes}">${name}</span>】的<span class="info">${
      buyCount.value
    }</span>份商品吗？（本次售出获得<span class="gold">${formatNumberWithCommas(totalPrice)}</span>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(async () => {
      const id = commerceId
      const count = buyCount.value
      await socketEmitAsync({ type: 'sellCommerceItem', data: { id, count } })
      const commerce = commerceList.value?.find((item) => item.commerceId === id)
      if (!commerce) return ElMessage.warning('未找到商会')
      const userCommerce = userStore.commerceList?.find((item) => item.id === id)
      if (!userCommerce) return
      userCommerce.count -= count
      const totalPrice = commerce.price * count
      userStore.gold += totalPrice
      tradeShow.value = false
      if (userCommerce.count === 0) userCommerce.avgPrice = 0
      getCurrentInstance()?.proxy?.$forceUpdate()
    })
    .catch(() => {})
}
function handleBuyMax() {
  if (!curCommerce.value) return
  if (tradeType.value === 'buy') {
    buyCount.value = Math.floor(userStore.gold / curCommerce.value.price)
  } else {
    buyCount.value += maxBuyCount
  }
}

const userTotalBuyCount = computed(() => {
  return userStore.commerceList.reduce((pre, item) => (pre += item.count), 0)
})
function getBuyCountById(id: number) {
  return userStore.commerceList.find((commerce) => commerce.id === id)?.count || 0
}
function getPersonalInfo(id: number) {
  return userStore.commerceList.find((commerce) => commerce.id === id)
}
const getUserTotal = computed(() => {
  return function (id: number) {
    if (!commerceList.value) return 0
    const item = commerceList.value.find((item) => item.commerceId === id)
    if (!item) return 0
    return (item.price || 0) * (getBuyCountById(id) || 0)
  }
})
const getIncome = computed(() => {
  return function (id: number) {
    if (!commerceList.value) return 0
    const item = commerceList.value.find((item) => item.commerceId === id)
    if (!item) return 0
    return Math.round((getPersonalInfo(id)?.avgPrice && (item.price - (getPersonalInfo(id)?.avgPrice || 0)) * getBuyCountById(id)) || 0)
  }
})
const tipShow = ref(true)
const localStorageKey = `war-soul-${envMode === 'test' ? 'test-' : ''}commerce-tip`
function neverShowTip() {
  localStorage.setItem(localStorageKey, '1')
}

const trendShow = ref(false)
function handleShowTrend(commerce: CommerceShowItem) {
  curCommerce.value = commerce
  trendShow.value = true
}

watch(
  () => commonStore.path,
  (val) => {
    if (val === 'Commerce' && !commerceList.value) {
      getCommerceList()
      setInterval(() => {
        const time = dayjs().format('HH:mm:ss')
        if (!['10:00:05', '14:00:05', '18:00:05', '22:00:05'].includes(time)) return
        getCommerceList()
      }, 1000)
    }
  },
)

onMounted(() => {
  if (localStorage.getItem(localStorageKey)) {
    tipShow.value = false
  }
})
</script>

<template>
  <div class="commerce-page page">
    <h2>
      商会
      <div class="help-info">
        <el-popover placement="bottom" width="100%" trigger="click">
          <template #reference>
            <el-icon size="20"><QuestionFilled /></el-icon>
          </template>
          <div class="affix">
            <h4>商会说明</h4>
            <p>
              1. 在商会中，您可以花费<span class="gold">金币</span
              >购买商会的商品，商品的价格会在每日10:00、14:00、18:00、22:00波动，您需要在合适的时机购入/售出商品
            </p>
            <p>
              2. 商会每次刷新的价格浮动倍率为 <span class="grow">-{{ envMode === 'test' ? 20 : 10 }}%</span> ~
              <span class="shrink">{{ envMode === 'test' ? 25 : 12 }}%</span>。
            </p>
            <p>3. 重要信息说明：</p>
            <p style="text-indent: 2ch">当前价格：该商会当前一份商品的价格</p>
            <p style="text-indent: 2ch">持有总额：您当前持有的份额 * 商品当前价格</p>
            <p style="text-indent: 2ch">持有收益：(当前价格 - 持有均价) * 持有份额</p>
            <p>4. <b>投资有风险，三思而后行</b></p>
          </div>
        </el-popover>
      </div>
    </h2>
    <p style="display: flex; justify-content: space-between; margin: 0 0 10px 0">
      <span>购入限额：{{ userTotalBuyCount }} / {{ maxBuyCount }}</span>
      <span>💰 {{ formatNumberWithCommas(userStore.gold) }}</span>
    </p>
    <el-alert type="warning" style="margin: 10px 0" @close="neverShowTip" v-show="tipShow">投资有风险，三思而后行</el-alert>
    <div class="commerce-list affix" v-loading="commonStore.loading">
      <div class="item border-wrap" v-for="item of commerceList" :key="item.commerceId">
        <h4 :class="item.classes">{{ item.name }}</h4>
        <p>
          <span>
            当前价格：
            <span :class="item.price * (1 + item.changeValue / 100) > item.price ? 'shrink' : 'grow'">
              {{ formatNumberWithCommas(item.price) }}（{{ item.changeValue }}%）
            </span>
          </span>
          <span>上次价格：{{ formatNumberWithCommas(Math.round(item.price / (1 + item.changeValue / 100))) }} </span>
        </p>
        <p>
          <span>持有总额：{{ formatNumberWithCommas(getUserTotal(item.commerceId)) }}</span>
          <span>持有份额：{{ formatNumberWithCommas(getBuyCountById(item.commerceId)) }}</span>
        </p>
        <p>
          <span>
            持有收益：
            <span :class="getIncome(item.commerceId) === 0 ? '' : getIncome(item.commerceId) > 0 ? 'shrink' : 'grow'">
              {{ formatNumberWithCommas(getIncome(item.commerceId)) }}
            </span>
          </span>
          <span>持有均价：{{ formatNumberWithCommas(Number((getPersonalInfo(item.commerceId)?.avgPrice || 0).toFixed(2))) }}</span>
        </p>
        <p>
          <span>
            持有收益率：
            <span :class="getIncome(item.commerceId) === 0 ? '' : getIncome(item.commerceId) > 0 ? 'shrink' : 'grow'">
              {{
                formatNumberWithCommas(
                  Number(((getUserTotal(item.commerceId) / (getUserTotal(item.commerceId) - getIncome(item.commerceId)) - 1) * 100).toFixed(2)),
                ) || 0
              }}%
            </span>
          </span>
        </p>
        <div class="action">
          <el-button size="small" type="success" plain @click="handleShowTrade('buy', item)">购入</el-button>
          <el-button size="small" type="warning" plain @click="handleShowTrade('sell', item)">卖出</el-button>
          <el-button size="small" type="primary" plain @click="handleShowTrend(item)">趋势</el-button>
        </div>
      </div>
    </div>

    <el-dialog v-model="tradeShow" width="100%" style="max-width: 500px">
      <template v-if="curCommerce">
        <h4>{{ tradeType === 'buy' ? '购入' : '售出' }}</h4>
        <p>持有份额：{{ getBuyCountById(curCommerce.commerceId) }}</p>
        <p style="padding-left: 20px">💰 {{ tradeType === 'buy' ? '-' : '+' }}{{ formatNumberWithCommas(curCommerce?.price * buyCount) }}</p>
        <div style="margin: 10px 0; text-align: center">
          <el-input-number
            v-model="buyCount"
            :precision="0"
            :min="0"
            :max="tradeType === 'buy' ? maxBuyCount - userTotalBuyCount : getBuyCountById(curCommerce.commerceId)"
            style="width: 85%"
          ></el-input-number>
        </div>

        <el-row :gutter="10">
          <el-col :span="6" v-for="item of buyCountBtn" :key="item" style="text-align: center">
            <el-button type="success" size="small" plain @click="buyCount += item">+{{ item }}</el-button>
          </el-col>
          <el-col :span="6" style="text-align: center">
            <el-button type="success" size="small" plain @click="handleBuyMax">最大</el-button>
          </el-col>
        </el-row>
        <br />
        <el-row :gutter="10">
          <el-col :span="6" v-for="item of buyCountBtn" :key="item" style="text-align: center">
            <el-button type="warning" size="small" plain @click="buyCount -= item">-{{ item }}</el-button>
          </el-col>
          <el-col :span="6" style="text-align: center">
            <el-button type="warning" size="small" plain @click="buyCount -= maxBuyCount">归零</el-button>
          </el-col>
        </el-row>
        <br />
        <div style="text-align: center">
          <el-button type="primary" plain @click="tradeType === 'buy' ? handleBuy() : handleSell()">确定</el-button>
        </div>
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="tradeShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="trendShow" width="100%" style="max-width: 500px" destroy-on-close>
      <Trend :commerce-id="curCommerce?.commerceId || 1" />
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="trendShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.commerce-page {
  position: relative;
  padding-top: 0;
  display: flex;
  flex-flow: column nowrap;
  height: 100%;
  overflow: hidden;
  font-size: 14px;
  h4 {
    font-size: 16px;
  }
  .help-info {
    position: absolute;
    top: 20px;
    right: 40px;
  }
  .commerce-list {
    display: flex;
    flex-flow: column nowrap;
    overflow: auto;
    flex: 1;
    .item {
      padding: 10px;
      margin-bottom: 10px;
      h4 {
        margin: 0 0 8px 0;
      }
      p {
        margin: 5px 0;
        display: flex;
        justify-content: space-between;
      }
    }
  }
  @media screen and (max-width: 330px) {
    font-size: 12px;
  }
}
</style>
